import type { Address, BaseMoney, CustomFieldsType, LineItem } from "@graphql/generated/types";
import { Attributes, CustomAttributes } from "@lib/enums/ProductAttributes";
import type { MoneyProps } from "@ecco/ui/money";
import { PRODUCT_IMAGE_PLACEHOLDER_URL } from "@ecco/ui/image-paths";
import { getProductAttributeValue } from "./attributesUtils";
import type { StoreData } from "@features/stores/types";
import { activeSiteConfig } from "@config/site/site-config";
import { roundPrice } from "./moneyUtils";
import type {
    ProductsAvailability,
    ProductsAvailabilityResponse,
} from "@lib/api-services/services/product-service/types";
import { ClientFetchService } from "@lib/helpers/clientFetchService";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { getOmnibusComparisonPrice, isOmniBusMarket } from "./omnibus";
import { hasSaleDiscount } from "./productUtility";

export type ShapedCustomField = {
    name: string;
    value: string;
};

export const getImageData = (item: LineItem, imageLabel: Attributes = Attributes.mainImage) => {
    const images = item?.variant?.images?.length
        ? item.variant.images
        : [
              {
                  url: PRODUCT_IMAGE_PLACEHOLDER_URL,
                  label: "placeholder",
              },
          ];
    return images.find((x) => x.label === imageLabel) ?? images[0];
};

export const getLineItemAttribute = (item: LineItem, name: Attributes, locale?: string): any => {
    return getProductAttributeValue(item?.variant?.attributesRaw, name, locale);
};

const getDiscountedPrice = (item: LineItem, forOnePiece?: boolean): BaseMoney | null =>
    forOnePiece
        ? item?.price?.discounted?.value
        : getCustomField(item, CustomAttributes.LINE_ITEM_DISCOUNTED_PRICE);

// Strikes through the omnibus reference, or the selling price when a promo code took it lower.
const getComparisonPriceBefore = (item: LineItem, forOnePiece?: boolean): BaseMoney | undefined => {
    const unitPriceBefore = getOmnibusComparisonPrice(item.price);
    if (!unitPriceBefore) return undefined;

    const quantity = forOnePiece ? 1 : item.quantity;
    const priceBefore = unitPriceBefore.centAmount * quantity;
    const priceNow =
        getDiscountedPrice(item, forOnePiece)?.centAmount ??
        (item.price?.value?.centAmount ?? 0) * quantity;

    return priceBefore > priceNow ? { ...unitPriceBefore, centAmount: priceBefore } : undefined;
};

export const getPriceBefore = (
    item: LineItem,
    locale: string,
    forOnePiece?: boolean
): MoneyProps => {
    // Not on sale: no RRP, only a promo code against the selling price
    if (isOmniBusMarket() || !hasSaleDiscount(item.price)) {
        return mapToMoney(getComparisonPriceBefore(item, forOnePiece), locale);
    }

    const discountedPrice = forOnePiece
        ? getCustomField(item.price, CustomAttributes.ADJUSTED_PRICE)
        : getCustomField(item, CustomAttributes.LINE_ITEM_DISCOUNTED_PRICE);
    if (!discountedPrice) {
        return null;
    }
    const value = forOnePiece
        ? getCustomField(item.price, CustomAttributes.RECOMMENDED_PRICE)
        : getCustomField(item, CustomAttributes.LINE_ITEM_PRICE);
    // A missing/zero full price (e.g. HEAP not returning itemListPrice for PL orders)
    // must not surface as a "0,00" strikethrough.
    if (!value || !value.centAmount) {
        return null;
    }
    // Backend sometimes returns a discounted price equal to the original (no real
    // discount). In rounding markets that still surfaces as a strikethrough because
    // getPriceNow rounds and getPriceBefore doesn't — suppress it here instead.
    if (value.centAmount === discountedPrice.centAmount) {
        return null;
    }
    return mapToMoney(value, locale);
};

export const getPriceNow = (item: LineItem, locale: string, forOnePiece?: boolean): MoneyProps => {
    const rawDiscountedPrice = getDiscountedPrice(item, forOnePiece);

    const discountedPrice =
        rawDiscountedPrice && activeSiteConfig.roundingUnit
            ? {
                  ...rawDiscountedPrice,
                  centAmount: roundPrice(rawDiscountedPrice.centAmount, "floor", item.quantity),
              }
            : rawDiscountedPrice;
    const price = forOnePiece
        ? item?.price?.value
        : getCustomField(item, CustomAttributes.LINE_ITEM_PRICE);
    const value = discountedPrice ?? price;
    return mapToMoney(value, locale);
};

export const getCustomField = <T extends { custom?: CustomFieldsType }>(
    item: T,
    fieldName: string,
    key?: string
) => {
    const field = item?.custom?.customFieldsRaw?.find((field) => field.name === fieldName);
    if (!field) return null;
    if (!key) return field.value;
    return field.value[key] ?? null;
};

const transformFieldValue = (fieldValue: unknown): ShapedCustomField["value"] => {
    if (!fieldValue) {
        return JSON.stringify("");
    }

    return JSON.stringify(fieldValue);
};

export const shapeCustomField = (
    fieldName: string,
    fieldValue?: unknown
): ShapedCustomField | undefined => {
    if (fieldValue) {
        return {
            name: fieldName,
            value: transformFieldValue(fieldValue),
        };
    }
};

export const shapeContactDetailsCustomFields = (shippingAddress: Address) => {
    const customFields: Record<string, unknown>[] = [];
    const vatField = getCustomField(shippingAddress, "address_CF_VatId");

    if (vatField) {
        customFields.push(shapeCustomField("address_CF_VatId", vatField));
    }

    const isCompanyField = getCustomField(shippingAddress, "address_CF_IsCompany");
    if (isCompanyField) {
        customFields.push(shapeCustomField("address_CF_IsCompany", isCompanyField));
    }

    return customFields;
};

export const shapeParcelShopCustomFields = (parcelShopData: StoreData) => {
    const customFields = [];
    if (parcelShopData) {
        customFields.push(shapeCustomField("address_CF_ParcelShopId", parcelShopData?.storeId));
        customFields.push(
            shapeCustomField("address_CF_ParcelShopCarrier", parcelShopData?.carrier)
        );
        customFields.push(
            shapeCustomField("address_CF_ParcelTypeCode", parcelShopData?.parcelTypeCode)
        );
    }
    return customFields;
};

export const shapeAddressCustomFields = (address, parcelShopData = null) => {
    const customFields = [];

    if (address?.phoneticFirstName) {
        customFields.push(
            shapeCustomField("address_CF_PhoneticFirstName", address?.phoneticFirstName)
        );
    }

    if (address?.phoneticLastName) {
        customFields.push(
            shapeCustomField("address_CF_PhoneticLastName", address?.phoneticLastName)
        );
    }

    if (address?.landmark) {
        customFields.push(shapeCustomField("address_CF_Landmark", address?.landmark));
    }

    if (address?.district) {
        customFields.push(shapeCustomField("address_CF_District", address?.district));
    }

    if (address?.deliveryInstructions) {
        customFields.push(
            shapeCustomField("address_CF_DeliveryInstructions", address?.deliveryInstructions)
        );
    }

    if (address.isCompany) {
        customFields.push(shapeCustomField("address_CF_IsCompany", Boolean(address?.isCompany)));
    }

    const vatIdCustomField = shapeCustomField("address_CF_VatId", address?.vatNumber);
    if (vatIdCustomField) {
        customFields.push(vatIdCustomField);
    }

    if (activeSiteConfig.checkout?.hasDHLExtraFields && address?.customerNumber) {
        customFields.push(shapeCustomField("address_CF_CustomerNumber", address?.customerNumber));
    }
    if (parcelShopData) {
        const shapedParcelShopData = shapeParcelShopCustomFields(parcelShopData);
        customFields.push(...shapedParcelShopData);
    }

    return customFields;
};

const mapToMoney = (value: BaseMoney, locale: string): MoneyProps => {
    if (!value) return null;

    return {
        locale: locale,
        data: {
            type: value.type,
            fractionDigits: value.fractionDigits,
            currencyCode: value.currencyCode,
            centAmount: value.centAmount,
        },
    };
};

export type CartItemsStock = {
    onStock: boolean;
    outOfStockSkus: string[];
};

export const AVAILABILITY_TIMEOUT_MS = 3000;

export const cartItemsOnStock = async (cartId: string, skus: string[]): Promise<CartItemsStock> => {
    try {
        const response = await ClientFetchService.post<
            ProductsAvailability,
            { availability?: ProductsAvailabilityResponse[] }
        >(
            "/api/product/availability",
            {
                skus,
                cartId: cartId,
            },
            { timeout: AVAILABILITY_TIMEOUT_MS }
        );
        const outOfStockSkus = (response?.availability ?? [])
            .filter((item) => item.isAvailable === false)
            .map((item) => item.sku);
        return { onStock: outOfStockSkus.length === 0, outOfStockSkus };
    } catch (error) {
        Logger.warn(ServiceType.WEB, "Error checking cart items stock availability", {
            error,
            status: (error as { status?: number } | null)?.status,
            tag: LogTag.PRODUCT,
            cartId,
            skus,
        });
        // Any non-2xx response or timeout must not block the shopper: treat every SKU as available.
        return { onStock: true, outOfStockSkus: [] };
    }
};
